import type { VideoDiagnosticLevel } from '@modelcast/types';

type SafeJson = string | number | boolean | null | SafeJson[] | { [key: string]: SafeJson };

export interface VideoDiagnosticContext {
  roomCode?: string | null;
  participantId?: string | null;
  participantName?: string | null;
  participantRole?: string | null;
}

interface VideoDiagnosticEntry extends VideoDiagnosticContext {
  sessionId: string;
  timestamp: string;
  level: VideoDiagnosticLevel;
  event: string;
  url: string | null;
  userAgent: string | null;
  visibilityState: string | null;
  online: boolean;
  details: SafeJson;
}

const MAX_BATCH_SIZE = 20;
const FLUSH_DELAY_MS = 1500;
const CLIENT_SESSION_STORAGE_KEY = 'drawclub.videoDiagnostics.sessionId';

const queue: VideoDiagnosticEntry[] = [];
let flushTimer: number | null = null;

function getClientSessionId() {
  try {
    const existing = sessionStorage.getItem(CLIENT_SESSION_STORAGE_KEY);
    if (existing) return existing;

    const id = crypto.randomUUID();
    sessionStorage.setItem(CLIENT_SESSION_STORAGE_KEY, id);
    return id;
  } catch {
    return `diagnostic-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function safeJson(value: unknown, depth = 0): SafeJson {
  if (value === null || value === undefined) return null;

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack?.slice(0, 1200) ?? null,
    };
  }

  if (typeof value === 'string') return value.slice(0, 1000);
  if (typeof value === 'number') return Number.isFinite(value) ? value : String(value);
  if (typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'function' || typeof value === 'symbol') return String(value);

  if (depth > 4) return '[MaxDepth]';

  if (Array.isArray(value)) {
    return value.slice(0, 30).map((item) => safeJson(item, depth + 1));
  }

  if (typeof value === 'object') {
    const result: Record<string, SafeJson> = {};
    for (const [key, childValue] of Object.entries(value).slice(0, 80)) {
      if (/token|secret|authorization|password|credential/i.test(key)) {
        result[key] = '[redacted]';
      } else {
        result[key] = safeJson(childValue, depth + 1);
      }
    }
    return result;
  }

  return String(value);
}

function scheduleFlush() {
  if (flushTimer !== null) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flushVideoDiagnostics();
  }, FLUSH_DELAY_MS);
}

function createVideoDiagnosticEntry(
  level: VideoDiagnosticLevel,
  event: string,
  details: unknown = null,
  context: VideoDiagnosticContext = {}
): VideoDiagnosticEntry {
  return {
    sessionId: getClientSessionId(),
    timestamp: new Date().toISOString(),
    level,
    event,
    roomCode: context.roomCode ?? null,
    participantId: context.participantId ?? null,
    participantName: context.participantName ?? null,
    participantRole: context.participantRole ?? null,
    url: window.location.href,
    userAgent: navigator.userAgent,
    visibilityState: document.visibilityState,
    online: navigator.onLine,
    details: safeJson(details),
  };
}

async function postVideoDiagnostics(events: VideoDiagnosticEntry[], useBeacon = false) {
  const body = JSON.stringify({ events });

  if (useBeacon && navigator.sendBeacon) {
    const sent = navigator.sendBeacon('/api/video-diagnostics', new Blob([body], { type: 'application/json' }));
    if (sent) return;
  }

  const response = await fetch('/api/video-diagnostics', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: body.length < 60_000,
  });

  if (!response.ok) {
    throw new Error(`Diagnostics request failed with ${response.status}`);
  }
}

export function logVideoDiagnostic(
  level: VideoDiagnosticLevel,
  event: string,
  details: unknown = null,
  context: VideoDiagnosticContext = {}
) {
  const entry = createVideoDiagnosticEntry(level, event, details, context);

  const consoleDetails = {
    roomCode: entry.roomCode,
    participantId: entry.participantId,
    participantName: entry.participantName,
    participantRole: entry.participantRole,
    details: entry.details,
  };

  if (level === 'error') {
    console.error(`[video] ${event}`, consoleDetails);
  } else if (level === 'warn') {
    console.warn(`[video] ${event}`, consoleDetails);
  } else if (level === 'debug') {
    console.debug(`[video] ${event}`, consoleDetails);
  } else {
    console.log(`[video] ${event}`, consoleDetails);
  }

  queue.push(entry);

  if (queue.length >= MAX_BATCH_SIZE) {
    void flushVideoDiagnostics();
  } else {
    scheduleFlush();
  }
}

export async function flushVideoDiagnostics(useBeacon = false) {
  if (flushTimer !== null) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }

  if (queue.length === 0) return;
  const events = queue.splice(0, MAX_BATCH_SIZE);

  try {
    await postVideoDiagnostics(events, useBeacon);
  } catch (error) {
    console.warn('[video] Failed to send diagnostics', error);
  }
}

export async function sendVideoDiagnosticReport(
  event: string,
  details: unknown,
  context: VideoDiagnosticContext = {}
) {
  const entry = createVideoDiagnosticEntry('info', event, details, context);
  await postVideoDiagnostics([entry]);
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    void flushVideoDiagnostics(true);
  });
}
